// dropUniqueConstraint — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'drop-unique-constraint', constraintName })` op
// (DSE2 §3.B3).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B3.
//
// Per-engine rules (mirrors DSE2's `synthesizeDropUniqueConstraint`):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mssql / pgsql / oracle: emit `DROP CONSTRAINT name`.
//   - mysql: emits `DROP INDEX name` — mysql stores UNIQUE constraints as
//     unique indexes; `DROP INDEX <name>` is the universally-supported drop
//     idiom (`DROP CONSTRAINT` is 8.0.19+).
//   - pgsql / oracle: optional `cascade?: boolean` appends `CASCADE`.

import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type DropUniqueConstraintArgs = {
  readonly constraintName: string;
  readonly cascade?: boolean;
};

export function buildDropUniqueConstraintEdit(args: DropUniqueConstraintArgs): DdlEdit {
  return {
    op: 'drop-unique-constraint',
    constraintName: args.constraintName,
    cascade: args.cascade,
  };
}

export async function commitDropUniqueConstraint(
  ctx: VqeDdlEditContext,
  args: DropUniqueConstraintArgs,
): Promise<void> {
  const edit = buildDropUniqueConstraintEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isDropUniqueConstraintSupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getDropUniqueConstraintUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … DROP UNIQUE. Recreate '
      + 'the table without the constraint — use “Edit as SQL” to write '
      + 'the change.'
    );
  }
  return '';
}

/** Cascade is accepted on the DROP CONSTRAINT clause for postgres + oracle.
 *  mysql emits `DROP INDEX name` and silently ignores cascade; mssql does
 *  not accept the keyword in this position. */
export function isCascadeSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

/** Pre-flight validator: every supported engine requires `constraintName`;
 *  cascade is rejected on mssql + mysql. */
export function validateDropUniqueConstraintForEngine(
  engine: SqlEngine,
  args: DropUniqueConstraintArgs,
): string | null {
  if (engine === 'sqlite') return getDropUniqueConstraintUnsupportedTooltip(engine);
  const name = args.constraintName.trim();
  if (name === '') {
    return `${engine} requires the constraint name to drop a UNIQUE constraint.`;
  }
  if (!isCascadeSupported(engine) && args.cascade === true) {
    return 'CASCADE is supported on PostgreSQL + Oracle only.';
  }
  return null;
}
